import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Network, MapPin, Share2, Code2, CheckCircle2, Copy, ExternalLink, Users, Globe2, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { api, fileUrl } from '../lib/api';
import { PageHeader, SectionHeader, Loading } from '../components/Shared';
import { PitaCTA, usePitaLogo } from '../components/PitaCTA';

const BADGE_LABELS = {
  supports: 'Supports the Tracker',
  shares: 'Shares the Tracker',
  embeds: 'Embeds the Tracker',
  uses_live: 'Uses the Tracker Live',
  network_member: 'Network Member',
};

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'mayor', label: 'Mayor' },
  { value: 'ward', label: 'Wards' },
  { value: 'active', label: 'Active Supporters' },
  { value: 'embedded', label: 'Embedded Live' },
  { value: 'shared', label: 'Shared by Link' },
];

const SupporterCard = ({ s }) => (
  <Card className="card-lift h-full" data-testid={`supporter-card-${s.id}`}>
    <CardContent className="p-5">
      <div className="flex items-start gap-3">
        {s.photo_url ? (
          <img src={fileUrl(s.photo_url)} alt={s.name} className="h-14 w-14 rounded-xl object-cover ring-1 ring-slate-200 shrink-0" />
        ) : (
          <div className="h-14 w-14 rounded-xl bg-[#1a2744]/10 flex items-center justify-center shrink-0"><Users className="h-6 w-6 text-[#1a2744]" /></div>
        )}
        <div className="min-w-0">
          <p className="font-serif font-bold text-[#1a2744] leading-snug">{s.name}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {s.office === 'mayor' ? 'Candidate for Mayor' : s.office === 'trustee' ? 'Candidate for Trustee' : `Candidate, Ward ${s.ward || '?'}`}
          </p>
          <Badge variant="outline" className="mt-1.5 border-[#C9A84C] text-[#7A5C12] bg-[#C9A84C]/10 text-[10px] font-bold">{BADGE_LABELS[s.badge] || s.badge}</Badge>
        </div>
      </div>
      {s.statement && <p className="text-sm text-slate-600 mt-3 leading-relaxed border-l-2 border-[#C9A84C]/50 pl-3 italic">“{s.statement}”</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {s.embed_live && <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]">Live</Badge>}
        {s.website_url && (
          <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#1a2744] underline underline-offset-2 inline-flex items-center gap-1" data-testid={`supporter-website-${s.id}`}>
            <ExternalLink className="h-3 w-3" /> Website
          </a>
        )}
        {(s.tracker_url || s.slug) && (
          <Link to={s.tracker_url || `/tracker/${s.slug}`} className="text-xs font-bold text-[#1a2744] underline underline-offset-2 inline-flex items-center gap-1" data-testid={`supporter-tracker-${s.id}`}>
            <MapPin className="h-3 w-3" /> Their Tracker
          </Link>
        )}
      </div>
    </CardContent>
  </Card>
);

export default function TrackerNetwork() {
  const [content, setContent] = useState(null);
  const [supporters, setSupporters] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const pitaLogo = usePitaLogo('network');

  useEffect(() => {
    Promise.all([
      api.get('/network/content').then((r) => setContent(r.data)),
      api.get('/network/supporters').then((r) => setSupporters(r.data || [])),
      api.get('/network/stats').then((r) => setStats(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading || !content) return <Loading />;

  const filtered = supporters.filter((s) => {
    if (filter === 'mayor') return s.office === 'mayor';
    if (filter === 'ward') return s.office === 'ward';
    if (filter === 'active') return ['uses_live', 'embeds', 'network_member'].includes(s.badge) || s.embed_live;
    if (filter === 'embedded') return s.embed_live;
    if (filter === 'shared') return s.badge === 'shares';
    return true;
  });

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const embedCode = `<iframe src="${origin}/embed/tracker/ward-8?mode=compact" width="100%" height="420" style="border:0;border-radius:16px;overflow:hidden" title="Public Issue Tracker App (PITA)" loading="lazy"></iframe>`;

  const copyEmbed = () => {
    navigator.clipboard.writeText(embedCode).then(() => toast.success('Embed code copied'));
  };

  return (
    <div data-testid="tracker-network-page">
      <PageHeader kicker="THE MODEL CAN GROW" title={content.title} subtitle={content.tagline} logo={pitaLogo?.url} logoAlt={pitaLogo?.alt} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        {/* Core message */}
        <div className="bg-[#1a2744] rounded-2xl p-6 sm:p-10" data-testid="network-message">
          <Network className="h-7 w-7 text-[#C9A84C]" />
          <p className="text-white text-lg sm:text-xl font-serif font-bold leading-relaxed mt-3 max-w-3xl">{content.tagline}</p>
          <p className="text-slate-300 mt-3 leading-relaxed max-w-3xl">{content.message}</p>
          {stats && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="network-stats">
              {[['Participating wards', stats.participating_wards], ['Issues tracked', stats.total_tracked], ['Resolved', stats.resolved], ['Public supporters', stats.supporters_count]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-white/5 border border-white/10 p-3 text-center">
                  <p className="text-2xl font-black text-[#C9A84C]">{v}</p>
                  <p className="text-[11px] text-slate-300 font-semibold mt-0.5">{l}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Supporters */}
        <div className="mt-12">
          <SectionHeader kicker="OPT-IN ONLY" title={content.supporters_title} dek={content.supporters_subtitle} />
          <div className="flex flex-wrap gap-2 mb-5" data-testid="supporter-filters">
            {FILTERS.map((f) => (
              <button key={f.value} onClick={() => setFilter(f.value)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${filter === f.value ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300 hover:border-[#1a2744]'}`}
                data-testid={`supporter-filter-${f.value}`}>
                {f.label}
              </button>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="supporters-empty">
              <ShieldCheck className="h-8 w-8 text-[#C9A84C] mx-auto" />
              <p className="text-slate-600 mt-3 max-w-xl mx-auto leading-relaxed text-sm">{content.supporters_empty}</p>
              <Button asChild className="mt-4 h-11 px-5 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="supporters-contact-btn">
                <Link to="/tracker-network/join">Request to Join the Network</Link>
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="supporters-grid">
              {filtered.map((s) => <SupporterCard key={s.id} s={s} />)}
            </div>
          )}
        </div>

        {/* Toolkit */}
        <div className="mt-12">
          <SectionHeader kicker="EMBED / SHARE TOOLKIT" title={content.toolkit_title} dek={content.toolkit_body} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="h-full" data-testid="toolkit-hosted">
              <CardContent className="p-6">
                <Share2 className="h-6 w-6 text-[#C9A84C]" />
                <h3 className="font-serif font-black text-[#1a2744] text-lg mt-3">Option A: Direct Hosted Link</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">Approved candidates get a clean hosted tracker link for their ward or campaign, with no website changes needed. Examples:</p>
                <ul className="mt-3 space-y-1.5 text-sm font-mono text-[#1a2744]">
                  <li className="bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-200">/tracker/ward-1</li>
                  <li className="bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-200">/tracker/ward-6</li>
                  <li className="bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-200">/tracker/mayor</li>
                </ul>
                <Button asChild variant="outline" className="mt-4 h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press w-full sm:w-auto" data-testid="toolkit-hosted-demo">
                  <Link to="/tracker/ward-8"><ExternalLink className="h-4 w-4 mr-1.5" /> See the Ward 8 hosted page</Link>
                </Button>
              </CardContent>
            </Card>
            <Card className="h-full" data-testid="toolkit-embed">
              <CardContent className="p-6">
                <Code2 className="h-6 w-6 text-[#C9A84C]" />
                <h3 className="font-serif font-black text-[#1a2744] text-lg mt-3">Option B: Embedded Widget</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{content.how_it_works}</p>
                <pre className="mt-3 bg-[#0d1526] text-emerald-300 text-[11px] rounded-xl p-3 overflow-x-auto whitespace-pre-wrap break-all" data-testid="embed-code-block">{embedCode}</pre>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <Button onClick={copyEmbed} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="copy-embed-btn">
                    <Copy className="h-4 w-4 mr-1.5" /> Copy Embed Code
                  </Button>
                  <Button asChild variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="embed-preview-link">
                    <a href="/embed/tracker/ward-8?mode=compact" target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-1.5" /> Preview Widget</a>
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-3">Responsive, privacy-safe reporting with tracking numbers and public map access. Candidate branding is added only after approval.</p>
              </CardContent>
            </Card>
          </div>
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between" data-testid="network-how-it-works">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700 font-semibold">{content.contact_line}</p>
            </div>
            <Button asChild className="h-11 px-5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press shrink-0" data-testid="network-contact-btn">
              <Link to="/tracker-network/join">Request to Join</Link>
            </Button>
          </div>
        </div>

        {/* PITA calls to action + sharing */}
        <div className="mt-12">
          <PitaCTA />
        </div>

        {/* Citywide teaser */}
        <div className="mt-12 rounded-2xl border border-[#C9A84C]/40 bg-gradient-to-br from-[#1a2744] to-[#22345c] p-6 sm:p-8" data-testid="network-citywide-teaser">
          <Globe2 className="h-6 w-6 text-[#C9A84C]" />
          <h3 className="text-white font-serif font-black text-xl sm:text-2xl mt-3">{content.citywide_title}</h3>
          <p className="text-slate-300 mt-2 max-w-2xl leading-relaxed">{content.citywide_message}</p>
          <Button asChild className="mt-4 h-11 px-5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="citywide-link-btn">
            <Link to="/across-kawartha-lakes">See the Citywide View</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
